import raw from '@/james-playground/aa-family-claim/claim/aa1-registry.json';

/**
 * The AA1 11/25 form, question by question, as the claim reads it.
 *
 * Generated from the two registry parts by
 * `assistant/tasks/attendance-allowance-2026-09-28/aa-pathways/build_registry.py`:
 * every numbered question and sub-part, every widget it writes to, and the
 * condition under which the form asks it. Entries without a Q number are the
 * facts the form needs but never asks for (where she is staying, whether the
 * account has a roll number) and the few the product adds (existing awards,
 * good and bad days).
 *
 * Nothing here is product copy. What the family sees is in `asks.ts`.
 */

export type AnswerType =
  | 'yes_no'
  | 'single_choice'
  | 'multi_choice'
  | 'text'
  | 'long_text'
  | 'date'
  | 'approx_date'
  | 'number'
  | 'count_per_day'
  | 'count_per_night'
  | 'minutes'
  | 'days_per_week'
  | 'nights_per_week'
  | 'address'
  | 'phone'
  | 'ni_number'
  | 'sort_code'
  | 'account_number'
  | 'signature'
  | 'table';

/**
 * How much an answer matters. The first two make up "ready to send"; the third
 * is "make it stronger"; optional answers count toward neither.
 */
export type Requiredness =
  | 'required_to_process'
  | 'needed_for_decision'
  | 'strengthens_claim'
  | 'optional';

export type Condition =
  | { all: Condition[] }
  | { any: Condition[] }
  | { id: string; op: 'eq' | 'ne' | 'has'; value: string }
  | { id: string; op: 'filled' | 'empty'; perRow?: boolean }
  | { id: string; op: 'in'; set: 'EEA_OR_SWISS' }
  | { fn: 'addressInWales' };

export type EntryOption = {
  label: string;
  /** The value the PDF widget takes when this option is chosen. */
  exportValue: string;
  widget?: string;
};

export type CaptureHint =
  | 'voice'
  | 'typed'
  | 'pick'
  | 'profile'
  | 'prescription'
  | 'hospital_letter'
  | 'care_plan';

export type Entry = {
  /** The official number, "Q31.4"; null for product entries. */
  q: string | null;
  id: string;
  product?: boolean;
  page?: number;
  section?: string;
  /** The form's own wording. */
  prompt?: string;
  /** A short name, for product entries that have no form wording. */
  label?: string;
  answerType: AnswerType;
  requiredness: Requiredness;
  mustConfirm: boolean;
  /** When the form asks this; absent means always. */
  when?: Condition;
  /** Widgets in row order for table columns. */
  widgets: string[];
  options?: EntryOption[];
  table?: {
    columns: string[];
    rows: string;
    columnIds: string[];
    cells?: { column: string; widget: string; id: string }[][];
  };
  /** The table this entry is a column of. */
  tableId?: string;
  captureHints?: CaptureHint[];
  inferable?: string;
  helpKind?: string;
  overflow?: string;
  officialHelp?: string;
  anomalies?: string;
};

export type BranchRule = {
  from: string;
  when: string;
  effect: string;
  wording: string;
  page: number;
};

type Registry = {
  form: string;
  version: string;
  sha256: string;
  entries: Entry[];
  branchRules: BranchRule[];
};

const registry = raw as unknown as Registry;

export const FORM = {
  name: registry.form,
  version: registry.version,
  sha256: registry.sha256,
};

export const ENTRIES: Entry[] = registry.entries;
export const BRANCH_RULES: BranchRule[] = registry.branchRules;

export const ENTRY: Record<string, Entry> = Object.fromEntries(
  ENTRIES.map((e) => [e.id, e]),
);

export const entry = (id: string): Entry => {
  const found = ENTRY[id];
  if (!found) throw new Error(`No AA1 registry entry "${id}"`);
  return found;
};

/** Signature boxes and their dates are left for the signer, never filled. */
const FORM_ONLY = new Set([
  'consentSignature',
  'consentSignatureDate',
  'supporterSignature',
  'supporterSignatureDate',
  'declarationSignature',
  'declarationDate',
  'submissionChecklist',
]);

export const isFormOnly = (e: Entry) =>
  FORM_ONLY.has(e.id) || e.answerType === 'signature';

/** Entries the product asks about: everything but signatures and table columns. */
export const isAsked = (e: Entry) => !isFormOnly(e) && !e.tableId;

/** The columns of a table entry, in the form's order. */
export const columnsOf = (tableId: string): Entry[] =>
  (entry(tableId).table?.columnIds ?? [])
    .map((id) => ENTRY[id])
    .filter((column): column is Entry => Boolean(column));

/** How many rows the form gives a table: "repeatable(6)" is six. */
export const rowsOf = (tableId: string): number => {
  const match = (entry(tableId).table?.rows ?? '').match(/\((\d+)\)/);
  return match ? Number(match[1]) : 1;
};

/** Bar 1 is "ready to send", bar 2 "make it stronger". */
export const barOf = (e: Entry): 0 | 1 | 2 =>
  e.requiredness === 'required_to_process' ||
  e.requiredness === 'needed_for_decision'
    ? 1
    : e.requiredness === 'strengthens_claim'
      ? 2
      : 0;
